import EditorJS from "@editorjs/editorjs";


// Define an object mapping block types to their corresponding dynamic import functions
const blocksUsed = {
    header: () => import("@editorjs/header"),
    delimiter: () => import("@editorjs/delimiter"),
    link: () => import("@editorjs/link"),
    RawTool: async () => {
        const RawTool = await import("@editorjs/raw");
        return {
            default: class CustomRawTool extends RawTool.default {
                render() {
                    const wrapper = document.createElement("div");
                    const htmlContent = this.data.html || this.data.text || "";
                    
                    // Insert HTML content
                    wrapper.innerHTML = htmlContent;
                    
                    // Only run script extraction and execution if there are actual script tags
                    if (htmlContent.includes("<script")) {
                        // Dynamically import script utilities only when needed
                        import("./scriptExecutor.js").then(({ extractScriptData, executeScriptsAfterInteraction }) => {
                            const scriptData = extractScriptData(wrapper);
                            if (scriptData.length > 0) {
                                executeScriptsAfterInteraction(scriptData, wrapper);
                            }
                        });
                    }
                    return wrapper;
                }
            }
        };
    },
    quote: () => import("@editorjs/quote"),
    table: () => import("@editorjs/table"),
    checklist: () => import("@editorjs/checklist"),
    image: () => import("./editorjs/GalleryImage"),
    nestedList: () => import("@editorjs/nested-list"),
    list: () => import("./editorjs/CustomEditorList.js"),
    unorderedList: () => import("@editorjs/list"),
    embed: () => import("@editorjs/embed"),
    alert: () => import("editorjs-alert"),
    iframe: async () => {
        const Iframe = (await import("@hammaadhrasheedh/editorjs-iframe")).default || (await import("@hammaadhrasheedh/editorjs-iframe"));
        return {
            default: class CustomIframeTool extends Iframe {
                render() {
                    const wrapper = super.render();
                    setTimeout(() => {
                        const iframe = wrapper.querySelector("iframe");
                        if (iframe) {
                            iframe.setAttribute("loading", "lazy");
                        }
                    }, 0);
                    return wrapper;
                }
            }
        };
    },
    marker: () => import("@editorjs/marker"),
    reusableBlock: () => import("./editorjs/ReusableBlock"),
    faqsBlock: () => import("./editorjs/FAQsBlock.js"),
};

// Getting tools in formatted way
function getModuleValues(module, blockName) {
    let values = null;
    switch (blockName) {
    case "table":
        values = {
            class: module.default || module,
            inlineToolbar: true,
        };
        break;

    case "image":
        values = {
            class: module.default || module,
            config: { readOnly: true },
        };
        break;

    case "marker":
        values = {
            class: module.default || module,
            shortcut: "CMD+SHIFT+M",
        };
        break;

    case "list":
        values = {
            class: module.default || module,
            inlineToolbar: true,
            config: {
                defaultStyle: "unordered",
            },
        };
        break;

    case "faqsBlock":
        values = {
            class: module.default || module,
            config: { readOnly: true },
        };
        break;

    default:
        values = {
            class: module.default || module,
        };
        break;
    }
    return values;
}

// Function to fetch and configure tools dynamically based on the block names used in the editor
async function getBlockConfig(blockNames) {
    const result = {};
    for (const blockName of blockNames) {
        if (blocksUsed[blockName]) {
            // Import the componnets dynamically
            const module = await blocksUsed[blockName]();
            result[blockName] = getModuleValues(module, blockName);
        }
    }
    return result;
}

// Function to initialize an individual EditorJS instance
async function initializeEditor(editorId, blocksId) {
    const data = JSON.parse(
        document.getElementById(blocksId).value.replace("&quot;", "") || "{}",
    );

    const blockTypes = data?.blocks.map((block) => block.type);
    const uniqueBlockTypes = [...new Set(blockTypes)];
    const tools = await getBlockConfig(uniqueBlockTypes);

    return new EditorJS({
        holder: editorId,
        readOnly: true,
        data: data,
        tools: tools,
        onReady: () => {
            // Remove event listeners interfering with selection
            document.querySelectorAll(`#${editorId} .ce-block`).forEach((block) => {
                // Skip if block has interactive content
                if (block.querySelector("iframe, video, audio, button")) return;
                block.addEventListener("mousedown", (e) => {
                    e.stopPropagation(); // Let browser handle selection
                }, true);
            });
        },
    });
}

// Function to initialize multiple EditorJS instances for a set of editors
async function initializeEditors(editors) {
    const editorInstances = {};

    for (const [index, editor] of editors.entries()) {
        if (document.getElementById(editor.editorId) != null) {
            editorInstances[`editor${index + 1}`] = await initializeEditor(
                editor.editorId,
                editor.blocksId,
            );
        }
    }

    return editorInstances;
}

// Add an event listener to initialize editors when the DOM content is fully loaded
window.addEventListener("DOMContentLoaded", async () => {
    // Fetch the list of editors from a global `window.editors` variable
    const editors = window.editors || [];

    // Initialize all editors and store their instances in a global `window.editorInstances` variable
    window.editorInstances = await initializeEditors(editors);
});
